import { clone, flow, getSnapshot, types, runInAction } from 'mobx-state-tree';
import { Interaction } from './Meeting';
import { ApiStore } from './ApiStore';
import { NullVideo, NullVideoChunk, Video, VideoChunck } from './VideoPresentation';
import * as Sentry from '@sentry/react';

const videoPresentation = types.model('VideoPresentationStore', {
    videoData: types.optional(Video, getSnapshot(NullVideo)),
    videoEtagData: types.optional(types.array(types.frozen()), []),
    videoChunck: types.optional(VideoChunck, getSnapshot(NullVideoChunk)),
    loading: types.optional(types.boolean, false),
    error: types.optional(types.string, ''),
    recordingType: types.optional(types.string, ''),
    uploadProgress: types.optional(types.string, 'Video Uploading...'),
    participants: types.optional(types.array(types.frozen()), []),
    interactions: types.optional(types.array(Interaction), [])  // Store MST model instances
});

export const VideoPresentationStore = types.compose(videoPresentation, ApiStore)
    .views(self => ({
        get getAppEtags() {
            return self.videoEtagData;
        }
    }
    ))
    .actions((self) => ({
        reset() {
            // Clear all state when switching between meeting types
            self.videoData = getSnapshot(NullVideo);
            self.videoEtagData.clear();
            self.videoChunck = getSnapshot(NullVideoChunk);
            self.loading = false;
            self.error = '';
            self.uploadProgress = 'Video Uploading...';
            self.participants.clear();
            self.interactions.clear();
            console.log('VideoPresentationStore: State cleared for new recording session');
        },
        setRecordingType(recordingType) {
            self.recordingType = recordingType;
        },
        setParticipants(participants) {
            self.participants = participants;
        },
        setInteractions(interactions) {
            // Now expects safe interaction snapshots (plain objects)
            self.interactions = interactions || [];
        },
        uploadInitialChunk: flow(function* uploadInitialChunk(data) {
            self.videoEtagData = [];
            const body = {
                userId: data.userId,
                ContentType: data.ContentType,
                numberOfTries: data.numberOfTries,
                companyId: data.companyId,
                interactions: getInteractionDtos(self.interactions),
                interactionId: data.interactionId  // Include interaction ID for Murphy question tracking
            };
            self.loading = true;
            self.error = '';
            try {
                const res = clone(NullVideo);
                yield self.postApi(`/companies/${data.companyId}/initialize-upload/${data.key}`, body, res);
                self.videoData = res;
            } catch (error) {
                console.error('Error fetching data:', error);
                self.error = error.message;
            } finally {
                self.loading = false;
            }
        }),
        uploadChunk: flow(function* uploadChunk(data, blob, isFinal = false) {
            // Validate that we have the necessary video data before proceeding
            if (!self.videoData || !self.videoData.key) {
                console.error('uploadChunk called without proper video data initialization. Call uploadInitialChunk first.');
                self.error = 'Video upload not initialized. Please start recording again.';
                return;
            }

            const body = { userId: data.userId, ContentType: data.ContentType, PartNumber: data.partNumber, uploadId: self.videoData.uploadId };
            self.loading = true;
            self.error = '';
            try {
                const res = clone(NullVideoChunk);
                const keyWithoutExtension = self.videoData.key.split('/')[1].split('.')[0];
                yield self.postApi(`/companies/${data.companyId}/initialize-upload/${keyWithoutExtension}`, body, res);
                self.videoChunck = res;
                if (res.uploadUrl) {
                    // Yield the XHR as a Promise so the flow awaits upload completion
                    yield new Promise((resolve, reject) => {
                        const xhr = new XMLHttpRequest();
                        xhr.open('PUT', self.videoChunck.uploadUrl);
                        xhr.onerror = () => reject(new Error('XHR network error'));
                        xhr.onreadystatechange = function () {
                            if (xhr.readyState === XMLHttpRequest.DONE) {
                                if (xhr.status === 200) {
                                    const ETag = xhr.getResponseHeader('ETag');
                                    self.addVideoEtagData({ PartNumber: data.partNumber, ETag: ETag.replace(/"/g, '') });
                                    resolve();
                                } else {
                                    reject(new Error(`Upload part ${data.partNumber} failed with status ${xhr.status}`));
                                }
                            }
                        };
                        xhr.send(blob);
                    });
                    if (isFinal) {
                        yield self.finalizeUpload(data);
                    }
                }
            } catch (error) {
                console.error('Error uploading chunk:', error);
                self.error = error.message;
            } finally {
                self.loading = false;
            }
        }),
        addVideoEtagData(item) {
            self.videoEtagData.push(item);
        },
        finalizeUpload: flow(function* finalizeUpload(data) {
            const body = { uploadId: self.videoData.uploadId, key: self.videoChunck.key, parts: self.videoEtagData };
            self.loading = true;
            self.error = '';
            if (Array.isArray(self.videoEtagData) && self.videoEtagData.length > 0) {
                try {
                    yield self.postApi(`/companies/${data.companyId}/finalize-upload`, body);
                    self.setUploadProgress('Video upload complete. Sending to Murphy for Grading');
                } catch (error) {
                    Sentry.captureException(error);
                    console.error('Error fetching data:', error);
                    self.error = error.message;
                } finally {
                    self.loading = false;
                }
            }
        }),
        setUploadProgress(progress) {
            self.uploadProgress = progress;
        },
        getQuestionAudio: flow(function* getQuestionAudio(questionTitle, voiceTag, format) {
            self.error = '';
            try {
                console.log(`Getting audio for question: "${questionTitle}" with voice: ${voiceTag}`);
                const response = yield self.getApi(`/presigned-url?assetType=murphy-audio&key=audio/questions/${questionTitle}_${voiceTag}.${format}`);

                const responseJSON = JSON.parse(response.body);

                if (!responseJSON.url) {
                    console.log(responseJSON);
                    throw new Error(`Failed to get signed URL for audio file!`);
                }

                return responseJSON.url; // Changed from signedUrl to url to match Lambda response
            } catch (error) {
                console.error('Error fetching audio URL:', error);
                self.error = error.message;
                return null;
            }
        })
    }));

const getInteractionDtos = (interactionItems) => {
    return interactionItems?.map(item => ({
        text: item.text,
        // title: item.title, // Uncomment if you have a title property
        participant: item.participantName
    }));
};

